import type { TvFeaturedWinner } from "./tv-mode";

/** How long the latest winner stays on the TV stage before the podium returns. */
export const TV_SPOTLIGHT_WINNER_MS = 25_000;
/** How long the podium stays up before the latest winner comes back. */
export const TV_SPOTLIGHT_PODIUM_MS = 20_000;
/** Matches the stage curtain close transition, so the swap happens behind closed curtains. */
export const TV_SPOTLIGHT_CURTAIN_MS = 1_700;

export type TvSpotlightView = "winner" | "podium";

export interface TvSpotlightState {
  view: TvSpotlightView;
  featuredKey: string | null;
  /** Epoch ms when the current view was chosen. */
  since: number;
}

export interface TvSpotlightInput {
  featuredKey: string | null;
  midShow: boolean;
  now: number;
  winnerMs: number;
  podiumMs: number;
}

/** A winner is out and categories remain, so the stage rotates between winner and podium. */
export function isTvMidShow(input: {
  announcedCount: number;
  totalCategories: number;
  featured: TvFeaturedWinner | null;
}): boolean {
  return (
    input.featured !== null &&
    input.announcedCount > 0 &&
    input.announcedCount < input.totalCategories
  );
}

export function tvFeaturedKey(featured: TvFeaturedWinner | null): string | null {
  return featured ? `${featured.categoryName}:${featured.winnerName}` : null;
}

function rotates(input: Pick<TvSpotlightInput, "featuredKey" | "midShow">): boolean {
  return input.midShow && input.featuredKey !== null;
}

export function initialTvSpotlight(input: TvSpotlightInput): TvSpotlightState {
  return {
    view: rotates(input) ? "winner" : "podium",
    featuredKey: input.featuredKey,
    since: input.now,
  };
}

/**
 * A new winner always takes the stage and restarts its hold. Otherwise the
 * view flips once its hold runs out. Returns the same object when nothing changes.
 */
export function advanceTvSpotlight(
  state: TvSpotlightState,
  input: TvSpotlightInput,
): TvSpotlightState {
  if (!rotates(input)) {
    if (state.view === "podium" && state.featuredKey === input.featuredKey) {
      return state;
    }
    return { view: "podium", featuredKey: input.featuredKey, since: input.now };
  }
  if (input.featuredKey !== state.featuredKey) {
    return { view: "winner", featuredKey: input.featuredKey, since: input.now };
  }
  const remaining = tvSpotlightRemainingMs(state, input);
  if (remaining !== null && remaining <= 0) {
    return {
      view: state.view === "winner" ? "podium" : "winner",
      featuredKey: state.featuredKey,
      since: input.now,
    };
  }
  return state;
}

/** Milliseconds until the next scheduled flip, or null when the stage should hold. */
export function tvSpotlightRemainingMs(
  state: TvSpotlightState,
  input: TvSpotlightInput,
): number | null {
  if (!rotates(input)) {
    return null;
  }
  const hold = state.view === "winner" ? input.winnerMs : input.podiumMs;
  return Math.max(0, state.since + hold - input.now);
}
